/**
 * VG 2.0 · Phase 11A — ValutaGenio per Property Advisor
 * Conversion surface for the Property Advisor audience.
 *
 * Rules honoured:
 *  - No new product domain (§62). Consumes existing capabilities.
 *  - Single primary CTA: "Richiedi una demo" (§26).
 *  - Real product surfaces represented with owner-safe demo copy (§35/§36).
 *  - No fake social proof, no invented pricing, no rating (§32/§33).
 *  - Commercial hierarchy: Caso strutturato → Call focalizzata → Esecuzione (§52).
 */
import React from "react";
import {
  ArrowRight, Check, Circle, ChevronRight,
  Lightbulb, Users, Compass, LogIn, MapPin, Handshake,
} from "lucide-react";
import Logo from "@/components/landing/Logo";
import Footer from "@/components/landing/Footer";
import { submitPaDemoRequest } from "@/lib/vg2PaLanding";

// Property Advisor landing assets (owner-provided, stored under /public).
const PA_IMG = "/images/property-advisor";

// ---------- SEO metadata (no react-helmet dependency) ---------------------

function useLandingSEO() {
  React.useEffect(() => {
    const prevTitle = document.title;
    document.title =
      "ValutaGenio per Property Advisor · Entra nel caso già strutturato";
    const metas = [
      {
        name: "description",
        content:
          "ValutaGenio ti consegna un caso immobiliare strutturato prima della call: obiettivo del proprietario, quadro economico, decisione e piano d'azione. Focus sulla conversazione professionale e sull'esecuzione.",
      },
      { property: "og:title", content: "ValutaGenio per Property Advisor" },
      {
        property: "og:description",
        content:
          "Entra nel caso sapendo già da dove partire. Un caso strutturato, una call focalizzata, un percorso verso l'esecuzione.",
      },
      { property: "og:type", content: "website" },
    ];
    const attached = [];
    metas.forEach((m) => {
      const key = m.name ? "name" : "property";
      const val = m.name || m.property;
      let el = document.querySelector(`meta[${key}="${val}"]`);
      let created = false;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(key, val);
        document.head.appendChild(el);
        created = true;
      }
      const prev = el.getAttribute("content");
      el.setAttribute("content", m.content);
      attached.push({ el, prev, created });
    });
    let canonical = document.querySelector('link[rel="canonical"]');
    let canonicalCreated = false;
    const prevCanonical = canonical ? canonical.getAttribute("href") : null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
      canonicalCreated = true;
    }
    canonical.setAttribute("href", "/property-advisor");
    return () => {
      document.title = prevTitle;
      attached.forEach(({ el, prev, created }) => {
        if (created) el.remove();
        else if (prev !== null) el.setAttribute("content", prev);
      });
      if (canonicalCreated) canonical.remove();
      else if (prevCanonical !== null) canonical.setAttribute("href", prevCanonical);
    };
  }, []);
}

// ---------- reusable atoms -------------------------------------------------

function SectionEyebrow({ children }) {
  return (
    <span className="inline-block text-[11px] uppercase tracking-[0.22em] font-semibold text-[#B40101] mb-4">
      {children}
    </span>
  );
}

function SectionTitle({ children, id }) {
  return (
    <h2
      id={id}
      className="font-serif text-[#0A0A0A] text-3xl md:text-4xl lg:text-[42px] leading-[1.15] tracking-tight max-w-3xl"
    >
      {children}
    </h2>
  );
}

function SectionLead({ children }) {
  return (
    <p className="mt-6 text-[#0A0A0A]/80 text-[17px] md:text-[18px] leading-relaxed max-w-2xl">
      {children}
    </p>
  );
}

function PillarCard({ number, title, body }) {
  return (
    <div className="p-7 md:p-8 rounded-2xl border border-[#E5E7EB] bg-white hover:border-[#B40101]/40 transition-colors">
      <div className="flex items-center gap-3 mb-4">
        <span className="w-8 h-8 rounded-full border-2 border-[#B40101] text-[#B40101] font-semibold text-sm inline-flex items-center justify-center">
          {number}
        </span>
        <h3 className="font-semibold text-[#0A0A0A] text-lg">{title}</h3>
      </div>
      <p className="text-[#0A0A0A]/80 text-[15px] leading-relaxed">{body}</p>
    </div>
  );
}

// ---------- navbar (bounded, no dashboard links) ---------------------------

function LandingNavbar() {
  return (
    <header
      data-testid="pa-landing-navbar"
      className="sticky top-0 z-50 w-full bg-white/95 backdrop-blur-sm border-b border-[#E5E7EB]"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
        <a href="/" data-testid="pa-landing-logo-home" className="flex items-center">
          <Logo size="text-xl md:text-2xl" />
        </a>
        <nav className="flex items-center gap-3 md:gap-6 text-sm">
          <a
            href="#sostegno"
            data-testid="pa-landing-nav-funziona"
            className="hidden md:inline text-[#0A0A0A]/70 hover:text-[#B40101] transition-colors"
          >
            Come funziona
          </a>
          <a
            href="#adesione"
            data-testid="pa-landing-nav-adesione"
            className="hidden md:inline text-[#0A0A0A]/70 hover:text-[#B40101] transition-colors"
          >
            Adesione
          </a>
          <a
            href="/property-advisor/login"
            data-testid="pa-landing-nav-login"
            className="inline-flex items-center gap-2 bg-[#B40101] hover:bg-[#8F0101] text-white font-medium text-sm px-4 py-2 rounded-lg transition-colors"
          >
            <LogIn className="w-4 h-4" /> Accedi
          </a>
        </nav>
      </div>
    </header>
  );
}

// ---------- HERO -----------------------------------------------------------

function Hero() {
  return (
    <section
      data-testid="pa-landing-hero"
      className="relative bg-[#F9F9F9] border-b border-[#E5E7EB] overflow-hidden"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-16 md:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-6">
          <SectionEyebrow>ValutaGenio per Property Advisor</SectionEyebrow>
          <h1
            data-testid="pa-landing-hero-h1"
            className="font-serif text-[#0A0A0A] text-4xl sm:text-5xl lg:text-[60px] leading-[1.05] tracking-tight"
          >
            Le opportunità immobiliari iniziano dai dati.
          </h1>
          <p className="mt-7 text-[#0A0A0A]/80 text-[17px] md:text-[18px] leading-relaxed max-w-xl">
            ValutaGenio aiuta i professionisti a individuare opportunità, comprendere gli obiettivi dei clienti e accompagnare le operazioni immobiliari.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
            <a
              href="#demo"
              data-testid="pa-landing-hero-cta-primary"
              className="inline-flex items-center gap-2 bg-[#B40101] hover:bg-[#8F0101] text-white font-semibold text-base px-6 py-3.5 rounded-xl transition-all duration-200 hover:-translate-y-0.5 shadow-[0_10px_30px_rgba(180,1,1,0.28)]"
              aria-label="Richiedi una demo"
            >
              Richiedi una demo <ArrowRight className="w-5 h-5" />
            </a>
            <a
              href="#funziona"
              data-testid="pa-landing-hero-cta-secondary"
              className="inline-flex items-center gap-2 text-[#0A0A0A] hover:text-[#B40101] font-medium text-base px-2 py-2 transition-colors"
            >
              Guarda come funziona <ChevronRight className="w-5 h-5" />
            </a>
          </div>
          <p className="mt-6 text-[13px] text-[#4B5563]">
            Nessun costo. Nessun impegno commerciale. Ti mostriamo il prodotto.
          </p>
        </div>

        <div className="lg:col-span-6">
          <HeroProductCard />
        </div>
      </div>
    </section>
  );
}

// Real product surface reproduced editorially. Uses demo-safe strings, no
// PII, no admin IDs, no raw internal enums (§35/§36, §49, §52).
function HeroProductCard() {
  return (
    <div
      data-testid="pa-landing-hero-product-card"
      className="relative rounded-2xl border border-[#E5E7EB] bg-white shadow-[0_20px_50px_-25px_rgba(0,0,0,0.15)] overflow-hidden"
      aria-label="Rappresentazione del Caso PA in ValutaGenio"
    >
      <div className="px-5 md:px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between bg-[#F9F9F9]">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#B40101]" />
          <span className="text-[13px] font-medium text-[#0A0A0A]">Caso · Property Advisor Workspace</span>
        </div>
        <span className="text-[11px] uppercase tracking-widest text-[#4B5563]">Pronto per la call</span>
      </div>
      <div className="p-5 md:p-6 space-y-5">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#4B5563] mb-1">Obiettivo del proprietario</div>
          <div className="text-[#0A0A0A] text-[15px] leading-relaxed">Capire se conviene vendere ora o attendere sei mesi.</div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[#4B5563] mb-1">Immobile</div>
            <div className="text-[#0A0A0A] text-[14px]">Appartamento · 82 m² · zona semicentrale</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[#4B5563] mb-1">Decisione</div>
            <div className="text-[#0A0A0A] text-[14px]">Vendita — timing 3–6 mesi</div>
          </div>
        </div>
        <div className="rounded-xl bg-[#F9F9F9] border border-[#E5E7EB] p-4">
          <div className="text-[11px] uppercase tracking-widest text-[#B40101] mb-2 font-semibold">Punti da affrontare nella call</div>
          <ul className="space-y-2 text-[14px] text-[#0A0A0A]">
            <li className="flex gap-2"><Circle className="w-3 h-3 mt-1.5 text-[#B40101] fill-[#B40101] shrink-0" /> Conformità edilizia da verificare.</li>
            <li className="flex gap-2"><Circle className="w-3 h-3 mt-1.5 text-[#B40101] fill-[#B40101] shrink-0" /> Preparazione visite entro 3 settimane.</li>
            <li className="flex gap-2"><Circle className="w-3 h-3 mt-1.5 text-[#B40101] fill-[#B40101] shrink-0" /> Aspettativa di prezzo da riallineare.</li>
          </ul>
        </div>
        <div className="flex items-center justify-between pt-1">
          <div className="text-[13px] text-[#0A0A0A]/70">
            <span className="font-medium text-[#0A0A0A]">Piano d&apos;azione</span> · prossima azione definita
          </div>
          <span className="inline-flex items-center gap-1 text-[12px] text-[#B40101] font-semibold">
            <Check className="w-3.5 h-3.5" /> Handoff completato
          </span>
        </div>
      </div>
    </div>
  );
}

// ---------- PROBLEM BEFORE THE CALL ---------------------------------------

function ProblemSection() {
  const items = [
    "Obiettivo del proprietario poco chiaro.",
    "Informazioni frammentate su immobile e documenti.",
    "Assunzioni mescolate a fatti.",
    "Focus prematuro sulla transazione.",
    "Nessun percorso esplicito verso l'esecuzione.",
  ];
  return (
    <section className="bg-white border-b border-[#E5E7EB]">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24">
        <SectionEyebrow>Il problema prima della call</SectionEyebrow>
        <SectionTitle>Ricostruire il contesto da zero, ogni volta.</SectionTitle>
        <SectionLead>
          Una conversazione immobiliare inizia spesso da qui. ValutaGenio struttura il quadro <em>prima</em> che il Property Advisor entri nel caso, senza sostituirsi al ruolo professionale.
        </SectionLead>
        <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 max-w-3xl">
          {items.map((t) => (
            <li key={t} className="flex items-start gap-3 text-[#0A0A0A] text-[15px] leading-relaxed">
              <span className="w-1.5 h-1.5 mt-2.5 rounded-full bg-[#B40101] shrink-0" />
              {t}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// ---------- STRUCTURED CASE (product flow visual) -------------------------

function StructuredCaseSection() {
  const steps = [
    { k: "CAPISCI", label: "Obiettivo e contesto" },
    { k: "VALUTA", label: "Quadro economico" },
    { k: "DECIDI", label: "Decisione del proprietario" },
    { k: "AGISCI", label: "Piano d'azione" },
    { k: "EVA", label: "Chiarimenti e handoff" },
  ];
  return (
    <section
      id="funziona"
      data-testid="pa-landing-funziona"
      className="bg-[#F9F9F9] border-b border-[#E5E7EB]"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24">
        <SectionEyebrow>Un caso già strutturato</SectionEyebrow>
        <SectionTitle>Cinque passaggi prima che il PA prenda in carico.</SectionTitle>
        <SectionLead>
          Il proprietario completa un percorso guidato. Al termine, il Caso è pronto per il Property Advisor: obiettivo, valutazione orientativa, decisione esplicita e piano d&apos;azione condiviso.
        </SectionLead>

        <div className="mt-12 rounded-2xl border border-[#E5E7EB] bg-white p-6 md:p-8">
          <ol className="flex flex-col md:flex-row md:items-start gap-3 md:gap-1 md:justify-between">
            {steps.map((s, i) => (
              <React.Fragment key={s.k}>
                <li className="flex-1 min-w-0 flex md:flex-col items-center md:items-start gap-3 md:gap-2">
                  <div className="w-8 h-8 shrink-0 rounded-full border-2 border-[#B40101] text-[#B40101] inline-flex items-center justify-center text-[13px] font-semibold">
                    {i + 1}
                  </div>
                  <div>
                    <div className="text-[13px] font-semibold text-[#0A0A0A] uppercase tracking-wide">{s.k}</div>
                    <div className="text-[13px] text-[#0A0A0A]/70">{s.label}</div>
                  </div>
                </li>
                {i < steps.length - 1 && (
                  <div className="hidden md:flex flex-1 items-center justify-center pt-3">
                    <div className="h-px w-full bg-[#E5E7EB]" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </ol>
          <div className="mt-8 flex items-center justify-center">
            <div className="inline-flex items-center gap-3 rounded-full bg-[#B40101] text-white px-5 py-2 text-sm font-semibold">
              <ArrowRight className="w-4 h-4" /> Handoff al Property Advisor
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- THREE PILLARS -------------------------------------------------

function PillarsSection() {
  return (
    <section className="bg-white border-b border-[#E5E7EB]">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24">
        <SectionEyebrow>I tre vantaggi</SectionEyebrow>
        <SectionTitle>Struttura, focus, esecuzione.</SectionTitle>
        <SectionLead>
          Ogni funzionalità di ValutaGenio serve una di queste tre promesse. Se non le supporta, non ha spazio in questo prodotto.
        </SectionLead>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          <PillarCard
            number="1"
            title="Caso strutturato"
            body="Sai cosa vuole il proprietario, cosa è già emerso, cosa è stato valutato, cosa è stato deciso e cosa resta aperto — prima della call."
          />
          <PillarCard
            number="2"
            title="Call focalizzata"
            body="La prima conversazione parte da dubbi, evidenze, prerequisiti e cambi materiali. Non ricostruisci il contesto: lo confermi."
          />
          <PillarCard
            number="3"
            title="Esecuzione coordinata"
            body="Piano d'azione, ruoli professionali, rete propria o VG Extended Network, Project Circle: il caso avanza in modo tracciabile."
          />
        </div>
      </div>
    </section>
  );
}

// ---------- WHAT REACHES THE PA -------------------------------------------

function WhatReachesSection() {
  const bullets = [
    "Obiettivo del proprietario, in parole sue.",
    "Contesto dell'immobile e riferimenti economici disponibili.",
    "Orientamento di valore basato sui dati locali.",
    "Direzione scelta (vendita, locazione, valutazione, valorizzazione).",
    "Piano d'azione con prossima azione esplicita.",
    "Prerequisiti da chiudere e documenti da verificare.",
    "Punti aperti e preoccupazioni non ancora risolte.",
    "Handoff EVA con 1–3 focus consigliati per la call.",
  ];
  return (
    <section className="bg-[#F9F9F9] border-b border-[#E5E7EB]">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
        <div className="lg:col-span-6">
          <SectionEyebrow>Cosa arriva al Property Advisor</SectionEyebrow>
          <SectionTitle>Un fascicolo di contesto, non una lista di lead.</SectionTitle>
          <SectionLead>
            Il PA riceve un Caso, non un contatto. Le informazioni sono organizzate per il lavoro professionale, non per la vendita.
          </SectionLead>
        </div>
        <div className="lg:col-span-6">
          <ul className="rounded-2xl bg-white border border-[#E5E7EB] p-6 md:p-8 space-y-3">
            {bullets.map((b) => (
              <li key={b} className="flex items-start gap-3 text-[#0A0A0A] text-[15px]">
                <Check className="w-5 h-5 text-[#B40101] shrink-0 mt-0.5" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

// ---------- PA WORKFLOW ---------------------------------------------------

function WorkflowSection() {
  const steps = [
    { title: "Apri il caso", body: "Contesto già leggibile in un minuto, con struttura coerente." },
    { title: "Leggi i punti essenziali", body: "Obiettivo, decisione, prerequisiti, punti aperti." },
    { title: "Prepara la call", body: "Focus consigliati e domande residue evidenziate." },
    { title: "Parla con il proprietario", body: "Concentri il tempo su decisioni e prossimi passi, non sul recupero informazioni." },
    { title: "Registra fatti ed evidenze", body: "Aggiungi voci strutturate: fatto verificato, informazione dichiarata, domanda aperta, aggiornamento documento." },
    { title: "Se qualcosa di materiale cambia, il caso viene rivalutato", body: "La rivalutazione è esplicita, non silenziosa. L'analisi precedente resta tracciata." },
    { title: "Prepara l'esecuzione", body: "Ruoli professionali, rete e Circle, azione successiva." },
  ];
  return (
    <section className="bg-white border-b border-[#E5E7EB]">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24">
        <SectionEyebrow>Come lavora il PA</SectionEyebrow>
        <SectionTitle>Sette passaggi, un solo caso.</SectionTitle>
        <ol className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl">
          {steps.map((s, i) => (
            <li key={s.title} className="flex gap-5">
              <div className="shrink-0 w-9 h-9 rounded-full border-2 border-[#B40101] text-[#B40101] font-semibold inline-flex items-center justify-center text-sm">
                {i + 1}
              </div>
              <div>
                <div className="font-semibold text-[#0A0A0A]">{s.title}</div>
                <div className="text-[#0A0A0A]/75 text-[15px] leading-relaxed mt-1">{s.body}</div>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

// ---------- FACTS + REASSESSMENT ------------------------------------------

function ReassessmentSection() {
  return (
    <section className="bg-[#F9F9F9] border-b border-[#E5E7EB]">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <SectionEyebrow>Fatti e rivalutazione</SectionEyebrow>
          <SectionTitle>Le informazioni nuove non riscrivono in silenzio l&apos;analisi.</SectionTitle>
          <SectionLead>
            Se durante la call emerge un fatto materiale — una difformità edilizia, una scadenza fiscale, un intento del proprietario diverso — il caso può richiedere una rivalutazione esplicita.
          </SectionLead>
          <p className="mt-4 text-[#0A0A0A]/75 text-[15px] leading-relaxed max-w-2xl">
            La cronologia resta leggibile. Le decisioni precedenti non spariscono. Il PA resta professionalmente responsabile di ciò che entra nel caso.
          </p>
        </div>
        <div className="lg:col-span-5">
          <div className="rounded-2xl border border-[#B40101]/30 bg-white p-6 shadow-[0_20px_50px_-25px_rgba(180,1,1,0.15)]">
            <div className="text-[11px] uppercase tracking-widest text-[#B40101] font-semibold mb-3">
              Rivalutazione richiesta
            </div>
            <p className="text-[#0A0A0A] text-[15px] leading-relaxed">
              &ldquo;Nuovo fatto: conformità edilizia non attestata. Il piano d&apos;azione richiede una revisione prima di procedere con l&apos;esecuzione.&rdquo;
            </p>
            <div className="mt-4 flex items-center gap-2 text-[13px] text-[#4B5563]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#B40101]" />
              Ambito: DECISION · ACTION_PLAN
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- EXECUTION: NETWORK + CIRCLE -----------------------------------

function ExecutionSection() {
  return (
    <section className="bg-white border-b border-[#E5E7EB]">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24">
        <SectionEyebrow>Dalla decisione all&apos;esecuzione</SectionEyebrow>
        <SectionTitle>Ruoli professionali, non un marketplace.</SectionTitle>
        <SectionLead>
          Quando il piano d&apos;azione richiede altre competenze, il PA identifica i ruoli professionali necessari e sceglie chi coinvolgere. Può usare la propria rete o consultare la rete estesa VG.
        </SectionLead>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-[#E5E7EB] p-7 bg-white">
            <div className="text-[11px] uppercase tracking-widest text-[#4B5563] mb-3 font-semibold">
              VG Extended Network
            </div>
            <h3 className="font-semibold text-[#0A0A0A] text-lg mb-2">Un bacino professionale più ampio</h3>
            <p className="text-[#0A0A0A]/80 text-[15px] leading-relaxed">
              Un pool di professionisti verificati sul piano dell&apos;identità e dello stato professionale. Nessun ranking commerciale, nessuna vendita di contatti.
            </p>
          </div>
          <div className="rounded-2xl border border-[#B40101]/30 p-7 bg-[#F9F9F9]">
            <div className="text-[11px] uppercase tracking-widest text-[#B40101] mb-3 font-semibold">
              Project Circle
            </div>
            <h3 className="font-semibold text-[#0A0A0A] text-lg mb-2">I professionisti effettivamente coinvolti nel caso</h3>
            <p className="text-[#0A0A0A]/80 text-[15px] leading-relaxed">
              Il Circle riunisce solo i professionisti che il PA ha selezionato per quel progetto. Ruoli, responsabilità e stato di ciascun ingaggio restano tracciati.
            </p>
          </div>
        </div>

        <p className="mt-8 text-[13px] text-[#4B5563]">
          VG Extended Network ≠ Project Circle. Il primo è un bacino accessibile, il secondo è il gruppo di lavoro di un singolo Caso.
        </p>
      </div>
    </section>
  );
}

// ---------- DOSSIER --------------------------------------------------------

function DossierSection() {
  return (
    <section className="bg-[#F9F9F9] border-b border-[#E5E7EB]">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <SectionEyebrow>Dossier professionale</SectionEyebrow>
          <SectionTitle>Un documento derivato dal caso, non una nuova analisi.</SectionTitle>
          <SectionLead>
            Dopo aver rivisto il caso e aver registrato le evidenze necessarie, il PA può generare un Dossier professionale in PDF: quadro dell&apos;immobile, obiettivo, decisione, piano d&apos;azione, elementi verificati, punti aperti, professionisti coinvolti e prossima azione.
          </SectionLead>
          <p className="mt-4 text-[#0A0A0A]/75 text-[15px] leading-relaxed max-w-2xl">
            Il Dossier non ri-esegue l&apos;analisi. Fotografa lo stato del caso al momento della generazione, con le fonti dichiarate.
          </p>
        </div>
        <div className="lg:col-span-6">
          <div className="rounded-2xl border border-[#E5E7EB] bg-white p-6 md:p-8 shadow-[0_20px_50px_-25px_rgba(0,0,0,0.15)]">
            <div className="flex items-center justify-between mb-5">
              <div className="text-[11px] uppercase tracking-widest text-[#4B5563] font-semibold">
                Dossier · Property Advisor
              </div>
              <span className="text-[11px] text-[#B40101] font-semibold">PDF</span>
            </div>
            <div className="space-y-3 text-[14px] text-[#0A0A0A]">
              <div className="flex items-start gap-3"><Check className="w-4 h-4 text-[#B40101] mt-1 shrink-0" /> Situazione dell&apos;immobile</div>
              <div className="flex items-start gap-3"><Check className="w-4 h-4 text-[#B40101] mt-1 shrink-0" /> Obiettivo del proprietario</div>
              <div className="flex items-start gap-3"><Check className="w-4 h-4 text-[#B40101] mt-1 shrink-0" /> Decisione e piano d&apos;azione</div>
              <div className="flex items-start gap-3"><Check className="w-4 h-4 text-[#B40101] mt-1 shrink-0" /> Elementi verificati e punti aperti</div>
              <div className="flex items-start gap-3"><Check className="w-4 h-4 text-[#B40101] mt-1 shrink-0" /> Professionisti coinvolti e prossima azione</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- WHO IT IS FOR / NOT FOR ---------------------------------------

function AudienceSection() {
  const forItems = [
    "Professionisti che lavorano direttamente con proprietari immobiliari.",
    "Advisor che gestiscono decisioni prima della transazione.",
    "Professionisti che coordinano casi multidisciplinari.",
    "Chi vuole passare dalla consulenza all&apos;esecuzione organizzata.",
  ];
  const notForItems = [
    "Chi cerca liste di contatti freddi.",
    "Chi cerca un CRM generico.",
    "Chi cerca un portale di annunci.",
    "Chi cerca ranking automatici dei professionisti.",
  ];
  return (
    <section className="bg-white border-b border-[#E5E7EB]">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-7">
          <SectionEyebrow>Per chi è</SectionEyebrow>
          <SectionTitle>Se lavori sul caso, non sul lead.</SectionTitle>
          <ul className="mt-8 space-y-3">
            {forItems.map((t) => (
              <li key={t} className="flex items-start gap-3 text-[#0A0A0A] text-[16px]">
                <Check className="w-5 h-5 text-[#B40101] mt-0.5 shrink-0" />
                <span dangerouslySetInnerHTML={{ __html: t }} />
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-5">
          <div className="rounded-2xl border border-[#E5E7EB] bg-[#F9F9F9] p-6">
            <div className="text-[11px] uppercase tracking-widest text-[#4B5563] font-semibold mb-4">
              Non è per te se cerchi
            </div>
            <ul className="space-y-2 text-[14px] text-[#0A0A0A]/80">
              {notForItems.map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <span className="text-[#4B5563] mt-0.5">—</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- SUBSCRIPTION VALUE + 3-GENERATION PROGRAM --------------------

function useRefParam() {
  return React.useMemo(() => {
    try {
      const u = new URL(window.location.href);
      const ref = u.searchParams.get("ref");
      return ref && ref.trim() ? ref.trim() : null;
    } catch {
      return null;
    }
  }, []);
}

function signupHref(ref) {
  return ref ? `/signup?ref=${encodeURIComponent(ref)}` : "/signup";
}

function SubscriptionAndProgramSection() {
  const ref = useRefParam();
  return (
    <section
      id="abbonamento"
      data-testid="pa-landing-subscription"
      className="bg-white border-b border-[#E5E7EB]"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24">
        <SectionEyebrow>Abbonamento e programma referral</SectionEyebrow>
        <SectionTitle>Un abbonamento chiaro e una rete che riconosce chi la costruisce.</SectionTitle>
        <SectionLead>
          Property Advisor entra in ValutaGenio con un abbonamento mensile. Quando invita altri professionisti, ne accompagna la crescita e riceve una quota delle loro quote effettivamente pagate — su tre generazioni, secondo lo status raggiunto.
        </SectionLead>

        {/* --- PRICING CARD --- */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div
            data-testid="pa-landing-subscription-card"
            className="rounded-2xl border border-[#E5E7EB] bg-[#F9F9F9] p-6 md:p-8"
          >
            <div className="text-[11px] uppercase tracking-widest text-[#B40101] font-semibold">Abbonamento Property Advisor</div>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="font-serif text-[#0A0A0A] text-5xl md:text-6xl">79&nbsp;€</span>
              <span className="text-[#0A0A0A]/70">/ mese · IVA esclusa</span>
            </div>
            <p className="mt-4 text-[#0A0A0A]/80 text-[15px] leading-relaxed">
              Include l&apos;intero motore VG (CAPISCI, VALUTA, DECIDI, AGISCI, EVA), la dashboard PA con i casi già strutturati, i documenti e le email owner, l&apos;accesso alla VG Extended Network e la costruzione del Project Circle. Nessun costo di setup, nessun impegno pluriennale.
            </p>
            <ul className="mt-5 space-y-2 text-[15px] text-[#0A0A0A]">
              <li className="flex gap-2"><Check className="w-4 h-4 mt-1 text-[#B40101] shrink-0" /> Casi strutturati prima della call.</li>
              <li className="flex gap-2"><Check className="w-4 h-4 mt-1 text-[#B40101] shrink-0" /> Handoff e rivalutazione tracciate.</li>
              <li className="flex gap-2"><Check className="w-4 h-4 mt-1 text-[#B40101] shrink-0" /> Rete professionale e Circle a supporto dell&apos;esecuzione.</li>
              <li className="flex gap-2"><Check className="w-4 h-4 mt-1 text-[#B40101] shrink-0" /> Piani semestrale e annuale con condizioni riservate al primo accesso.</li>
            </ul>
            <a
              href={signupHref(ref)}
              data-testid="pa-landing-signup-cta"
              className="mt-7 inline-flex items-center gap-2 bg-[#B40101] hover:bg-[#8F0101] text-white font-semibold text-base px-6 py-3.5 rounded-xl transition-colors"
            >
              Iscriviti come Property Advisor <ArrowRight className="w-5 h-5" />
            </a>
            {ref && (
              <p
                data-testid="pa-landing-ref-hint"
                className="mt-3 text-[13px] text-[#0A0A0A]/70"
              >
                Sei stato invitato con il codice <span className="font-semibold text-[#B40101]">{ref}</span>. Il codice viene preservato all&apos;iscrizione.
              </p>
            )}
          </div>

          {/* --- PROGRAM TIERS --- */}
          <div
            data-testid="pa-landing-referral-program"
            className="rounded-2xl border border-[#B40101]/20 bg-[#FFF6F6] p-6 md:p-8"
          >
            <div className="text-[11px] uppercase tracking-widest text-[#B40101] font-semibold">Programma referral 3 generazioni</div>
            <h3 className="mt-3 font-serif text-[#0A0A0A] text-2xl md:text-3xl">Bronzo, Oro, Diamante</h3>
            <p className="mt-3 text-[#0A0A0A]/80 text-[15px] leading-relaxed">
              Lo status dipende dal numero di <strong>diretti attivi</strong> — professionisti invitati da te che hanno completato l&apos;iscrizione, sono ancora abbonati e hanno superato la maturità minima di 60 giorni. Le percentuali si applicano al netto commissionabile di ciascun pagamento effettivo.
            </p>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <TierCard name="Bronzo" range="1–25 diretti attivi" g1="30%" g2="0%" g3="0%" />
              <TierCard name="Oro" range="26–50 diretti attivi" g1="50%" g2="5%" g3="0%" highlighted />
              <TierCard name="Diamante" range="51+ diretti attivi" g1="70%" g2="5%" g3="3%" />
            </div>
            <p className="mt-5 text-[13px] text-[#0A0A0A]/70 leading-relaxed">
              Nessuna promessa di reddito. Nessuna commissione oltre la terza generazione. Le percentuali si applicano solo su pagamenti effettivamente riscossi; i rimborsi Stripe stornano proporzionalmente le commissioni maturate.
            </p>
          </div>
        </div>

        {/* --- NET BASE + EXAMPLE --- */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-[#E5E7EB] bg-white p-6 md:p-8">
            <div className="text-[11px] uppercase tracking-widest text-[#4B5563] font-semibold">Base netta commissionabile</div>
            <h4 className="mt-3 font-serif text-[#0A0A0A] text-xl">Cosa entra effettivamente nel calcolo.</h4>
            <p className="mt-3 text-[#0A0A0A]/80 text-[15px] leading-relaxed">
              Per ogni pagamento la base è l&apos;importo effettivamente pagato dall&apos;abbonato meno l&apos;IVA applicabile e i costi Stripe pertinenti. Solo su questo netto si calcolano G1, G2 e G3.
            </p>
            <ul className="mt-4 space-y-2 text-[14px] text-[#0A0A0A]">
              <li className="flex gap-2"><Circle className="w-3 h-3 mt-1.5 text-[#B40101] fill-[#B40101] shrink-0" /> Nessun compenso su pagamenti falliti o in stato incompleto.</li>
              <li className="flex gap-2"><Circle className="w-3 h-3 mt-1.5 text-[#B40101] fill-[#B40101] shrink-0" /> Sconti applicati riducono la base.</li>
              <li className="flex gap-2"><Circle className="w-3 h-3 mt-1.5 text-[#B40101] fill-[#B40101] shrink-0" /> Rinnovi mensili generano nuove commissioni.</li>
            </ul>
          </div>
          <div
            data-testid="pa-landing-referral-example"
            className="rounded-2xl border border-[#E5E7EB] bg-white p-6 md:p-8"
          >
            <div className="text-[11px] uppercase tracking-widest text-[#4B5563] font-semibold">Esempio illustrativo</div>
            <h4 className="mt-3 font-serif text-[#0A0A0A] text-xl">Un rinnovo di un tuo diretto.</h4>
            <p className="mt-2 text-[13px] text-[#0A0A0A]/60">
              I valori qui sotto sono un&apos;illustrazione: le componenti fiscali e le commissioni Stripe reali dipendono dal Paese dell&apos;abbonato e dal metodo di pagamento.
            </p>
            <ul className="mt-4 space-y-1.5 text-[14px] text-[#0A0A0A]">
              <li>Prezzo lordo pagato: <strong>79,00&nbsp;€</strong></li>
              <li>IVA applicabile (illustrativa 22%): <strong>−14,24&nbsp;€</strong></li>
              <li>Costo Stripe illustrativo: <strong>−2,34&nbsp;€</strong></li>
              <li>Base netta illustrativa: <strong>62,42&nbsp;€</strong></li>
            </ul>
            <div className="mt-5 grid grid-cols-3 gap-2 text-[13px]">
              <ExampleTier label="Bronzo · G1 30%" value="≈ 18,73 €" />
              <ExampleTier label="Oro · G1+G2 50%+5%" value="≈ 31,21 € + 3,12 €" />
              <ExampleTier label="Diamante · G1+G2+G3 70%+5%+3%" value="≈ 43,69 € + 3,12 € + 1,87 €" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TierCard({ name, range, g1, g2, g3, highlighted }) {
  return (
    <div
      data-testid={`pa-landing-tier-${name.toLowerCase()}`}
      className={
        "rounded-xl p-4 border " +
        (highlighted
          ? "border-[#B40101] bg-white shadow-[0_10px_30px_-15px_rgba(180,1,1,0.35)]"
          : "border-[#E5E7EB] bg-white")
      }
    >
      <div className="text-[#0A0A0A] font-bold text-base">{name}</div>
      <div className="text-[12px] text-[#0A0A0A]/70 mb-3">{range}</div>
      <div className="text-[13px] text-[#0A0A0A] flex justify-between"><span>G1</span><strong>{g1}</strong></div>
      <div className="text-[13px] text-[#0A0A0A] flex justify-between"><span>G2</span><strong>{g2}</strong></div>
      <div className="text-[13px] text-[#0A0A0A] flex justify-between"><span>G3</span><strong>{g3}</strong></div>
    </div>
  );
}

function ExampleTier({ label, value }) {
  return (
    <div className="rounded-lg bg-[#F9F9F9] border border-[#E5E7EB] p-3">
      <div className="text-[11px] uppercase tracking-widest text-[#4B5563] font-semibold">{label}</div>
      <div className="mt-1 text-[#0A0A0A] font-semibold">{value}</div>
    </div>
  );
}

// ---------- DEMO REQUEST FORM ---------------------------------------------

function DemoRequestSection() {
  const [values, setValues] = React.useState({
    name: "",
    email: "",
    phone: "",
    professional_role: "Property Advisor",
    city: "",
    message: "",
  });
  const [state, setState] = React.useState({ status: "idle", error: null });

  const canSubmit =
    values.name.trim().length > 0 &&
    values.email.trim().length > 0 &&
    values.professional_role.trim().length > 0 &&
    state.status !== "submitting";

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setState({ status: "submitting", error: null });
    try {
      await submitPaDemoRequest(values);
      setState({ status: "done", error: null });
    } catch (err) {
      const message =
        err?.status === 429
          ? "Troppe richieste dalla tua rete. Riprova più tardi."
          : "Non è stato possibile inviare la richiesta. Riprova.";
      setState({ status: "error", error: message });
    }
  };

  const update = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.value }));

  return (
    <section
      id="demo"
      data-testid="pa-landing-demo"
      className="bg-[#F9F9F9] border-b border-[#E5E7EB]"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <SectionEyebrow>Richiedi una demo</SectionEyebrow>
          <SectionTitle>Vedi ValutaGenio applicato a un caso reale.</SectionTitle>
          <SectionLead>
            Ti mostriamo come si presenta un Caso strutturato, come si prepara una call focalizzata e come si passa all&apos;esecuzione. Nessun costo, nessun impegno.
          </SectionLead>
          <div className="mt-8 text-[13px] text-[#4B5563] space-y-1.5">
            <div className="flex items-center gap-2"><span className="w-1 h-1 bg-[#B40101] rounded-full" /> Prodotto reale, non un mockup.</div>
            <div className="flex items-center gap-2"><span className="w-1 h-1 bg-[#B40101] rounded-full" /> Ti contattiamo per fissare la sessione.</div>
            <div className="flex items-center gap-2"><span className="w-1 h-1 bg-[#B40101] rounded-full" /> I tuoi dati non entrano in campagne marketing.</div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {state.status === "done" ? (
            <SuccessCard onReset={() => {
              setValues({ name: "", email: "", phone: "", professional_role: "Property Advisor", city: "", message: "" });
              setState({ status: "idle", error: null });
            }} />
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid="pa-landing-demo-form"
              className="rounded-2xl border border-[#E5E7EB] bg-white p-6 md:p-8 space-y-4"
              noValidate
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field
                  id="name" label="Nome e cognome" required
                  value={values.name} onChange={update("name")}
                  data-testid="pa-landing-demo-name"
                />
                <Field
                  id="email" label="Email" type="email" required
                  value={values.email} onChange={update("email")}
                  data-testid="pa-landing-demo-email"
                />
                <Field
                  id="phone" label="Telefono (opzionale)"
                  value={values.phone} onChange={update("phone")}
                  data-testid="pa-landing-demo-phone"
                />
                <Field
                  id="professional_role" label="Ruolo professionale" required
                  value={values.professional_role} onChange={update("professional_role")}
                  data-testid="pa-landing-demo-role"
                />
                <Field
                  id="city" label="Città o area (opzionale)"
                  value={values.city} onChange={update("city")}
                  data-testid="pa-landing-demo-city"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-[13px] font-medium text-[#0A0A0A] mb-1.5">
                  Cosa vorresti vedere (opzionale)
                </label>
                <textarea
                  id="message"
                  data-testid="pa-landing-demo-message"
                  value={values.message}
                  onChange={update("message")}
                  rows={4}
                  maxLength={800}
                  className="w-full rounded-xl border border-[#E5E7EB] px-4 py-3 text-[15px] text-[#0A0A0A] placeholder-[#6B7280] focus:outline-none focus:ring-2 focus:ring-[#B40101]/40 focus:border-[#B40101]"
                  placeholder="Es. Un caso di vendita entro 6 mesi con documenti da chiarire."
                />
              </div>

              {state.error && (
                <div
                  role="alert"
                  data-testid="pa-landing-demo-error"
                  className="text-[14px] text-[#B40101] bg-[#B40101]/5 border border-[#B40101]/30 rounded-lg px-4 py-3"
                >
                  {state.error}
                </div>
              )}

              <div className="pt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <button
                  type="submit"
                  disabled={!canSubmit}
                  data-testid="pa-landing-demo-submit"
                  aria-label="Invia richiesta di demo"
                  className="inline-flex items-center gap-2 bg-[#B40101] hover:bg-[#8F0101] disabled:bg-[#B40101]/40 disabled:cursor-not-allowed text-white font-semibold text-base px-6 py-3.5 rounded-xl transition-colors"
                >
                  {state.status === "submitting" ? "Invio in corso…" : "Richiedi una demo"}
                  {state.status !== "submitting" && <ArrowRight className="w-5 h-5" />}
                </button>
                <p className="text-[12px] text-[#4B5563] max-w-sm">
                  Inviando la richiesta accetti di essere contattato da ValutaGenio. Nessun costo, nessuna registrazione automatica.
                </p>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ id, label, required, ...rest }) {
  return (
    <div>
      <label htmlFor={id} className="block text-[13px] font-medium text-[#0A0A0A] mb-1.5">
        {label}{required && <span className="text-[#B40101]"> *</span>}
      </label>
      <input
        id={id}
        required={required}
        {...rest}
        className="w-full rounded-xl border border-[#E5E7EB] px-4 py-3 text-[15px] text-[#0A0A0A] placeholder-[#6B7280] focus:outline-none focus:ring-2 focus:ring-[#B40101]/40 focus:border-[#B40101]"
      />
    </div>
  );
}

function SuccessCard({ onReset }) {
  return (
    <div
      data-testid="pa-landing-demo-success"
      className="rounded-2xl border border-[#B40101]/30 bg-white p-8 shadow-[0_20px_50px_-25px_rgba(180,1,1,0.15)]"
      role="status"
      aria-live="polite"
    >
      <div className="w-12 h-12 rounded-full bg-[#B40101]/10 inline-flex items-center justify-center mb-4">
        <Check className="w-6 h-6 text-[#B40101]" />
      </div>
      <h3 className="font-serif text-[#0A0A0A] text-2xl md:text-3xl">Richiesta ricevuta.</h3>
      <p className="mt-3 text-[#0A0A0A]/80 text-[16px] leading-relaxed">
        Ti contattiamo per mostrarti ValutaGenio applicato a un caso reale.
      </p>
      <button
        type="button"
        onClick={onReset}
        data-testid="pa-landing-demo-reset"
        className="mt-6 text-[14px] text-[#B40101] font-medium hover:underline"
      >
        Invia un&apos;altra richiesta
      </button>
    </div>
  );
}

// ========================================================================
// LAUNCH-L2 · New Property Advisor landing composition (owner-approved copy)
// Exact texts applied verbatim. Owner-provided imagery only.
// ========================================================================

function L2Navbar() {
  return (
    <header
      data-testid="pa-landing-navbar"
      className="sticky top-0 z-50 w-full bg-white/95 backdrop-blur-sm border-b border-[#E5E7EB]"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
        <a href="/" data-testid="pa-landing-logo-home" className="flex items-center">
          <Logo size="text-xl md:text-2xl" />
        </a>
        <nav className="flex items-center gap-3 md:gap-6 text-sm">
          <a
            href="#sostegno"
            data-testid="pa-landing-nav-funziona"
            className="hidden md:inline text-[#0A0A0A]/70 hover:text-[#B40101] transition-colors"
          >
            Come funziona
          </a>
          <a
            href="#adesione"
            data-testid="pa-landing-nav-adesione"
            className="hidden md:inline text-[#0A0A0A]/70 hover:text-[#B40101] transition-colors"
          >
            Adesione
          </a>
          <a
            href="/property-advisor/login"
            data-testid="pa-landing-nav-login"
            className="inline-flex items-center gap-2 bg-[#B40101] hover:bg-[#8F0101] text-white font-medium text-sm px-4 py-2.5 rounded-lg transition-colors"
          >
            <LogIn className="w-4 h-4" /> Accedi
          </a>
        </nav>
      </div>
    </header>
  );
}

// ---------- 01 · HERO ------------------------------------------------------

function L2Hero() {
  return (
    <section
      data-testid="pa-landing-hero"
      className="relative bg-white border-b border-[#E5E7EB] overflow-hidden"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 pt-14 md:pt-20 pb-14 md:pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-6">
          <SectionEyebrow>ValutaGenio per Property Advisor</SectionEyebrow>
          <h1
            data-testid="pa-landing-hero-h1"
            className="font-serif text-[#0A0A0A] text-4xl sm:text-5xl lg:text-[58px] leading-[1.06] tracking-tight"
          >
            Le opportunità immobiliari iniziano dai dati.
          </h1>
          <p className="mt-7 text-[#0A0A0A]/80 text-[18px] md:text-[20px] leading-relaxed max-w-xl">
            Incontra persone che hanno già esplorato il potenziale di un immobile e individuato una direzione. ValutaGenio ti offre un nuovo punto di partenza per trasformare l&apos;interesse in una consulenza di valore.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
            <a
              href="#sostegno"
              data-testid="pa-landing-hero-cta-primary"
              className="inline-flex items-center gap-2 bg-[#B40101] hover:bg-[#8F0101] text-white font-semibold text-base px-6 py-3.5 rounded-xl transition-all duration-200 hover:-translate-y-0.5 shadow-[0_10px_30px_rgba(180,1,1,0.28)]"
            >
              Scopri come funziona <ArrowRight className="w-5 h-5" />
            </a>
            <a
              href="#adesione"
              data-testid="pa-landing-hero-cta-secondary"
              className="inline-flex items-center gap-2 text-[#0A0A0A] hover:text-[#B40101] font-medium text-base px-2 py-2 transition-colors"
            >
              Diventa Property Advisor <ChevronRight className="w-5 h-5" />
            </a>
          </div>
        </div>
        <div className="lg:col-span-6">
          <div className="relative rounded-2xl overflow-hidden border border-[#E5E7EB] shadow-[0_24px_60px_-30px_rgba(0,0,0,0.3)] aspect-[4/3]">
            <img
              src={`${PA_IMG}/VG_PA_01_HERO.png`}
              alt="Professionista che analizza un immobile con ValutaGenio"
              data-testid="pa-landing-hero-image"
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>
        </div>
      </div>

      {/* narrative element — lightbulb */}
      <div className="bg-[#F9F9F9] border-t border-[#E5E7EB]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-10 md:py-14">
          <div className="flex items-start gap-5 max-w-4xl">
            <span className="shrink-0 w-12 h-12 rounded-full bg-[#B40101]/10 inline-flex items-center justify-center">
              <Lightbulb className="w-6 h-6 text-[#B40101]" />
            </span>
            <div>
              <h3 className="font-serif text-[#0A0A0A] text-2xl md:text-[28px] leading-snug">
                Una nuova opportunità professionale
              </h3>
              <p className="mt-3 text-[#0A0A0A]/80 text-[17px] md:text-[18px] leading-relaxed">
                Ogni giorno, persone e professionisti valutano immobili per venderli, acquistarli, affittarli o valorizzarli. Spesso hanno bisogno di chiarire le alternative prima di procedere. ValutaGenio li aiuta a esplorare le possibilità e a scegliere una direzione. Quando desiderano un confronto professionale, possono richiederlo. Per te significa poter incontrare persone che hanno già iniziato a ragionare su un&apos;operazione concreta.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- 02 · IL VANTAGGIO ---------------------------------------------

function L2Vantaggio() {
  const items = [
    { icon: MapPin, label: "Contesto" },
    { icon: Lightbulb, label: "Opportunità" },
    { icon: Handshake, label: "Relazione" },
  ];
  return (
    <section
      id="vantaggio"
      data-testid="pa-landing-vantaggio"
      className="bg-white border-b border-[#E5E7EB]"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <div className="relative rounded-2xl overflow-hidden border border-[#E5E7EB] shadow-[0_24px_60px_-30px_rgba(0,0,0,0.25)] aspect-[4/3]">
            <img
              src={`${PA_IMG}/VG_PA_02_VANTAGGIO.png`}
              alt="Prima conversazione tra Property Advisor e cliente"
              data-testid="pa-landing-vantaggio-image"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>
        <div className="lg:col-span-6 order-1 lg:order-2">
          <h2 className="font-serif text-[#0A0A0A] text-3xl md:text-4xl lg:text-[42px] leading-[1.15] tracking-tight">
            Meno tempo a ricostruire. Più tempo a consigliare.
          </h2>
          <div className="mt-6 flex items-center gap-3">
            <span className="shrink-0 w-10 h-10 rounded-full bg-[#B40101]/10 inline-flex items-center justify-center">
              <Users className="w-5 h-5 text-[#B40101]" />
            </span>
            <h3 className="font-semibold text-[#0A0A0A] text-xl">Più valore a ogni incontro</h3>
          </div>
          <p className="mt-5 text-[#0A0A0A]/80 text-[17px] md:text-[18px] leading-relaxed">
            Immagina una prima conversazione con una persona che ha già chiarito cosa vuole ottenere, esaminato una valutazione orientativa e confrontato diverse possibilità. Puoi dedicare più tempo alle domande importanti: quali verifiche servono, quali ostacoli affrontare e come procedere. La tecnologia prepara le informazioni; tu porti esperienza, capacità di giudizio e conoscenza del territorio.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-4">
            {items.map(({ icon: Icon, label }) => (
              <div key={label} className="flex flex-col items-center text-center gap-2">
                <span className="w-11 h-11 rounded-full border border-[#B40101]/30 inline-flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[#B40101]" />
                </span>
                <span className="text-[15px] md:text-[16px] font-medium text-[#0A0A0A]">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- 03 · FOCUS CENTRALE (grafico allegato 02) ---------------------

function L2Focus() {
  return (
    <section
      data-testid="pa-landing-focus"
      className="bg-[#F9F9F9] border-b border-[#E5E7EB]"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24">
        <h2 className="font-serif text-[#0A0A0A] text-3xl md:text-4xl lg:text-[42px] leading-[1.15] tracking-tight text-center max-w-4xl mx-auto">
          La tecnologia individua le possibilità. Tu le trasformi in realtà.
        </h2>
        <div className="mt-12 md:mt-16 px-2 sm:px-8 lg:px-16">
          <img
            src={`${PA_IMG}/grafico_allegato_02.png`}
            alt="Dal dato immobiliare all'incontro professionale: immobile, analisi dei dati, alternative immobiliari, incontro professionale"
            data-testid="pa-landing-focus-graphic"
            className="w-full h-auto mx-auto"
            loading="lazy"
          />
        </div>
      </div>
    </section>
  );
}

// ---------- 04 · COME FUNZIONA --------------------------------------------

function L2ComeFunziona() {
  const flow = ["VALUTA", "DECIDI", "AGISCI"];
  return (
    <section
      id="come-funziona"
      data-testid="pa-landing-funziona"
      className="bg-white border-b border-[#E5E7EB]"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-6">
          <SectionEyebrow>Come funziona</SectionEyebrow>
          <h2 className="font-serif text-[#0A0A0A] text-3xl md:text-4xl lg:text-[42px] leading-[1.15] tracking-tight">
            Dal potenziale alla consulenza.
          </h2>
          <p className="mt-6 text-[#0A0A0A]/80 text-[17px] md:text-[18px] leading-relaxed">
            L&apos;utente utilizza VALUTA e DECIDI, confronta le possibilità e sceglie una direzione. Se richiede un confronto, il Property Advisor consulta il caso autorizzato, approfondisce i passaggi necessari e può accompagnarlo verso la realizzazione dell&apos;operazione.
          </p>
          <div className="mt-9 flex items-center gap-2 md:gap-3">
            {flow.map((step, i) => (
              <React.Fragment key={step}>
                <span className="inline-flex items-center justify-center rounded-full border-2 border-[#B40101] text-[#B40101] font-semibold text-[13px] md:text-sm px-4 py-2 md:px-5 md:py-2.5">
                  {step}
                </span>
                {i < flow.length - 1 && (
                  <ArrowRight className="w-4 h-4 md:w-5 md:h-5 text-[#B40101] shrink-0" />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
        <div className="lg:col-span-6">
          <div className="relative rounded-2xl overflow-hidden border border-[#E5E7EB] shadow-[0_24px_60px_-30px_rgba(0,0,0,0.25)] aspect-[4/3]">
            <img
              src={`${PA_IMG}/VG_PA_03_COME_FUNZIONA.png`}
              alt="Il Property Advisor accompagna l'utente verso la realizzazione dell'operazione"
              data-testid="pa-landing-funziona-image"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- 05 · IL RUOLO DEL PROPERTY ADVISOR ----------------------------

function L2Ruolo() {
  return (
    <section
      id="ruolo"
      data-testid="pa-landing-ruolo"
      className="border-b border-[#E5E7EB]"
    >
      {/* parallax banner (accessible: static bg-fixed, honours reduced motion) */}
      <div
        className="relative bg-scroll md:bg-fixed bg-center bg-cover"
        style={{ backgroundImage: `url(${PA_IMG}/VG_PA_09_VISIONE_PARALLAX.webp)` }}
        data-testid="pa-landing-ruolo-parallax"
      >
        <div className="absolute inset-0 bg-[#0A0A0A]/55" aria-hidden="true" />
        <div className="relative max-w-[1200px] mx-auto px-5 md:px-8 py-24 md:py-36 text-center">
          <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/15 backdrop-blur-sm mb-6">
            <Compass className="w-7 h-7 text-white" />
          </span>
          <h2 className="font-serif text-white text-3xl md:text-5xl leading-[1.1] tracking-tight">
            Sostegno. Orientamento. Guida.
          </h2>
          <p className="mt-5 text-white/90 text-[18px] md:text-[22px] leading-relaxed max-w-2xl mx-auto">
            La visione giusta trasforma una possibilità in un&apos;opportunità.
          </p>
        </div>
      </div>

      <div className="bg-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <h3 className="font-serif text-[#0A0A0A] text-2xl md:text-3xl leading-snug">
              La competenza dà una direzione
            </h3>
            <p className="mt-5 text-[#0A0A0A]/80 text-[17px] md:text-[18px] leading-relaxed">
              Un immobile può offrire molte possibilità. Riconoscere quella più adatta richiede visione, capacità di ascolto ed esperienza. Il Property Advisor aiuta l&apos;utente a leggere la situazione, valutare i passaggi necessari e comprendere quali competenze coinvolgere. Il primo confronto è gratuito e senza obbligo di incarico. Se nasce l&apos;opportunità di collaborare, le condizioni vengono concordate direttamente tra le parti.
            </p>
            <a
              href="#adesione"
              data-testid="pa-landing-ruolo-cta"
              className="mt-8 inline-flex items-center gap-2 text-[#B40101] hover:text-[#8F0101] font-semibold text-base transition-colors"
            >
              Scopri come diventare Property Advisor <ArrowRight className="w-5 h-5" />
            </a>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-2xl overflow-hidden border border-[#E5E7EB] shadow-[0_24px_60px_-30px_rgba(0,0,0,0.25)] aspect-[7/4]">
              <img
                src={`${PA_IMG}/VG_PA_08_RELAZIONE.jpg`}
                alt="La relazione professionale tra Property Advisor e proprietario"
                data-testid="pa-landing-ruolo-image"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- 06 · ADESIONE --------------------------------------------------

function L2Adesione() {
  return (
    <section
      id="adesione"
      data-testid="pa-landing-adesione"
      className="bg-[#F9F9F9] border-b border-[#E5E7EB]"
    >
      <div className="max-w-[920px] mx-auto px-5 md:px-8 py-16 md:py-24">
        <SectionEyebrow>Adesione</SectionEyebrow>
        <h2 className="font-serif text-[#0A0A0A] text-3xl md:text-4xl lg:text-[42px] leading-[1.15] tracking-tight">
          Entra in ValutaGenio.
        </h2>
        <div className="mt-6 flex items-baseline gap-2">
          <span
            data-testid="pa-landing-price"
            className="font-serif text-[#0A0A0A] text-5xl md:text-6xl"
          >
            79&nbsp;€
          </span>
          <span className="text-[#0A0A0A]/70 text-lg">/ mese</span>
        </div>

        <p
          data-testid="pa-landing-adesione-claim"
          className="mt-7 font-serif text-[#1E3A8A] text-2xl md:text-[30px] leading-snug"
        >
          Entra in una piattaforma pensata per chi vuole sviluppare la propria attività professionale attraverso nuove opportunità immobiliari.
        </p>
        <p className="mt-5 text-[#0A0A0A]/80 text-[16px] md:text-[17px] leading-relaxed">
          ValutaGenio mette a tua disposizione un ambiente professionale integrato per sviluppare opportunità immobiliari, gestire i contatti e valorizzare la tua attività.
        </p>
        <p className="mt-4 font-semibold text-[#0A0A0A] text-[16px] md:text-[17px]">
          Con la tua adesione puoi accedere a:
        </p>
        <ul
          data-testid="pa-landing-features"
          className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4"
        >
          {[
            ["Dashboard professionale", "una panoramica del tuo lavoro, delle attività e delle opportunità."],
            ["Gestione Lead", "organizza e segui i contatti in ingresso."],
            ["Rete professionale", "sviluppa e gestisci la tua rete di collaborazioni."],
            ["Link personale e QR Code", "strumenti dedicati alla tua lead generation."],
            ["Social Coach AI", "supporto intelligente alla comunicazione sui social."],
            ["Strategy Coach AI", "strumenti di supporto alla strategia professionale."],
            ["Sara AI", "assistente intelligente a supporto della tua attività."],
          ].map(([label, desc]) => (
            <li key={label} className="flex items-start gap-3">
              <span className="shrink-0 mt-1 w-5 h-5 rounded-full bg-[#B40101]/10 inline-flex items-center justify-center">
                <Check className="w-3 h-3 text-[#B40101]" />
              </span>
              <span className="text-[15px] md:text-[16px] leading-snug text-[#0A0A0A]/80">
                <span className="font-semibold text-[#0A0A0A]">{label}</span>: {desc}
              </span>
            </li>
          ))}
        </ul>

        {/* referral — synthetic, no new figures, derived from existing copy */}
        <p
          data-testid="pa-landing-referral-synthetic"
          className="mt-6 text-[#0A0A0A]/70 text-[15px] md:text-[16px] leading-relaxed"
        >
          E c&apos;è un&apos;opportunità in più: invitando altri professionisti a entrare in ValutaGenio, puoi beneficiare del programma di riconoscimento economico previsto per la tua rete, con compensi calcolati sulle quote effettivamente pagate e secondo il livello raggiunto.
        </p>

        <div
          data-testid="pa-landing-referral-examples"
          className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5"
        >
          {[
            ["01", "4 professionisti iscritti", "Il tuo abbonamento si ripaga.", "I compensi referral possono compensare il costo mensile di ValutaGenio."],
            ["02", "10 professionisti iscritti", "La tua attività si finanzia.", "Un contributo ricorrente per strumenti, promozione e spese operative."],
            ["03", "30 professionisti iscritti", "Una seconda entrata professionale.", "Una rete attiva può generare un'entrata mensile aggiuntiva."],
          ].map(([num, tier, title, desc]) => (
            <div
              key={num}
              className="rounded-2xl border border-[#E5E7EB] bg-white p-5 md:p-6 flex flex-col"
            >
              <div className="flex items-baseline gap-2">
                <span className="font-serif text-[#B40101] text-2xl md:text-3xl leading-none">{num}</span>
                <span className="text-[#0A0A0A]/60 text-[13px] md:text-sm font-medium tracking-wide">— {tier}</span>
              </div>
              <h4 className="mt-4 font-semibold text-[#0A0A0A] text-[17px] md:text-[18px] leading-snug">{title}</h4>
              <p className="mt-2 text-[#0A0A0A]/70 text-[14px] md:text-[15px] leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>

        <p
          data-testid="pa-landing-referral-closing"
          className="mt-7 font-serif text-[#1E3A8A] text-xl md:text-2xl leading-snug"
        >
          ValutaGenio sostiene la tua attività, apre nuove opportunità nel mercato immobiliare e moltiplica le possibilità di acquisire nuovi clienti.
        </p>

        <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <a
            href="/signup"
            data-testid="pa-landing-signup-cta"
            className="inline-flex items-center gap-2 bg-[#B40101] hover:bg-[#8F0101] text-white font-semibold text-base px-6 py-3.5 rounded-xl transition-colors"
          >
            Iscriviti come Property Advisor <ArrowRight className="w-5 h-5" />
          </a>
          <a
            href="/codice-di-condotta"
            data-testid="pa-landing-conditions-cta"
            className="inline-flex items-center gap-2 text-[#0A0A0A] hover:text-[#B40101] font-medium text-base px-2 py-2 underline underline-offset-4 transition-colors"
          >
            Scopri le condizioni di adesione <ChevronRight className="w-5 h-5" />
          </a>
        </div>

        {/* foto finale della landing, dopo testi e CTA */}
        <div className="mt-12 md:mt-16">
          <div className="relative rounded-2xl overflow-hidden border border-[#E5E7EB] shadow-[0_24px_60px_-30px_rgba(0,0,0,0.25)] aspect-[16/9]">
            <img
              src={`${PA_IMG}/VG_PA_05_ADESIONE.png`}
              alt="Entra in ValutaGenio come Property Advisor"
              data-testid="pa-landing-adesione-image"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

// ========================================================================
// LAUNCH-L2 revisione · Tre blocchi narrativi (Sostegno · Orientamento · Guida)
// + parallax conclusivo. Testi definitivi del proprietario applicati VERBATIM.
// ========================================================================

// Reusable narrative block: desktop alternates photo/text; mobile = photo above text.
function L2Block({ id, testid, imageSide, image, alt, eyebrow, title, subtitle, paragraphs, children }) {
  const imgOrder = imageSide === "left" ? "lg:order-1" : "lg:order-2";
  const txtOrder = imageSide === "left" ? "lg:order-2" : "lg:order-1";
  return (
    <section
      id={id}
      data-testid={testid}
      className="bg-white border-b border-[#E5E7EB]"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className={`order-1 ${imgOrder} lg:col-span-6`}>
          <div className="relative rounded-2xl overflow-hidden border border-[#E5E7EB] shadow-[0_24px_60px_-30px_rgba(0,0,0,0.25)] aspect-[4/3]">
            <img
              src={image}
              alt={alt}
              data-testid={`${testid}-image`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>
        <div className={`order-2 ${txtOrder} lg:col-span-6`}>
          {eyebrow ? <SectionEyebrow>{eyebrow}</SectionEyebrow> : null}
          <h2 className="font-serif text-[#0A0A0A] text-3xl md:text-4xl lg:text-[42px] leading-[1.15] tracking-tight">
            {title}
          </h2>
          {subtitle ? (
            <h3 className="mt-4 font-semibold text-[#0A0A0A] text-xl">{subtitle}</h3>
          ) : null}
          <div className="mt-5 space-y-4">
            {paragraphs.map((p, i) => (
              <p key={i} className="text-[#0A0A0A]/80 text-[17px] md:text-[18px] leading-relaxed">
                {p}
              </p>
            ))}
          </div>
          {children}
        </div>
      </div>
    </section>
  );
}

function BloccoSostegno() {
  return (
    <L2Block
      id="sostegno"
      testid="pa-landing-sostegno"
      imageSide="left"
      image={`${PA_IMG}/VG_PA_02_VANTAGGIO.png`}
      alt="Prima conversazione tra Property Advisor e cliente"
      title="Meno tempo a ricostruire. Più tempo a consigliare."
      subtitle="Più valore a ogni incontro"
      paragraphs={[
        "Immagina una prima conversazione con una persona che ha già chiarito cosa vuole ottenere, esaminato una valutazione orientativa e confrontato diverse possibilità.",
        "Non devi ricostruire tutto da zero. Puoi concentrarti sulle domande importanti, sulle verifiche necessarie e sulle opportunità da sviluppare.",
        "È qui che comincia il Sostegno: ascoltare la persona, comprendere le sue esigenze e costruire un rapporto di fiducia.",
      ]}
    />
  );
}

function BloccoOrientamento() {
  const flow = ["VALUTA", "DECIDI", "AGISCI"];
  return (
    <L2Block
      id="orientamento"
      testid="pa-landing-orientamento"
      imageSide="right"
      image={`${PA_IMG}/VG_PA_03_COME_FUNZIONA.png`}
      alt="Il Property Advisor accompagna l'utente verso la realizzazione dell'operazione"
      title="Dal potenziale alla consulenza."
      paragraphs={[
        "L'utente utilizza VALUTA per analizzare l'immobile e DECIDI per confrontare le alternative e individuare una direzione.",
        "Quando desidera approfondire, può richiedere un primo confronto gratuito con un Property Advisor.",
        "Qui entra in gioco l'Orientamento: aiuti la persona a interpretare gli scenari individuati da ValutaGenio, chiarire le priorità e riconoscere le opportunità più interessanti.",
        "La tecnologia organizza le informazioni. La tua competenza permette di comprenderle e utilizzarle.",
      ]}
    >
      <div className="mt-8 flex items-center gap-2 md:gap-3">
        {flow.map((step, i) => (
          <React.Fragment key={step}>
            <span className="inline-flex items-center justify-center rounded-full border-2 border-[#B40101] text-[#B40101] font-semibold text-[13px] md:text-sm px-4 py-2 md:px-5 md:py-2.5">
              {step}
            </span>
            {i < flow.length - 1 && (
              <ArrowRight className="w-4 h-4 md:w-5 md:h-5 text-[#B40101] shrink-0" />
            )}
          </React.Fragment>
        ))}
      </div>
    </L2Block>
  );
}

function BloccoGuida() {
  return (
    <L2Block
      id="guida"
      testid="pa-landing-guida"
      imageSide="left"
      image={`${PA_IMG}/VG_PA_08_RELAZIONE.jpg`}
      alt="La relazione professionale tra Property Advisor e proprietario"
      title="La competenza dà una direzione."
      paragraphs={[
        "Un immobile può offrire molte possibilità. Riconoscere quella più adatta richiede esperienza, capacità di giudizio e conoscenza del territorio.",
        "La Guida è il passaggio dalla valutazione all'azione: metti a disposizione la tua esperienza per definire i passi concreti, individuare le competenze necessarie e accompagnare la persona nelle sue decisioni.",
        "La prima consulenza gratuita, di circa 30 minuti e senza obbligo di incarico, è pensata per offrire valore concreto e conquistare fiducia.",
        "Può diventare l'inizio di una relazione professionale: un incarico, un progetto da sviluppare o un percorso da seguire insieme.",
      ]}
    />
  );
}

// Parallax conclusivo — sintesi dei tre blocchi.
function ParallaxConclusivo() {
  return (
    <section
      data-testid="pa-landing-parallax"
      className="relative bg-scroll md:bg-fixed bg-center bg-cover border-b border-[#E5E7EB]"
      style={{ backgroundImage: `url(${PA_IMG}/VG_PA_09_VISIONE_PARALLAX.webp)` }}
    >
      <div className="absolute inset-0 bg-[#0A0A0A]/55" aria-hidden="true" />
      <div className="relative max-w-[1200px] mx-auto px-5 md:px-8 py-24 md:py-36 text-center">
        <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/15 backdrop-blur-sm mb-6">
          <Compass className="w-7 h-7 text-white" />
        </span>
        <h2 className="font-serif text-white text-3xl md:text-5xl leading-[1.1] tracking-tight">
          Sostegno. Orientamento. Guida.
        </h2>
        <p className="mt-5 text-white/90 text-[18px] md:text-[22px] leading-relaxed max-w-2xl mx-auto">
          La visione giusta trasforma una possibilità in un'opportunità.
        </p>
      </div>
    </section>
  );
}

// ---------- PAGE ----------------------------------------------------------

export default function PropertyAdvisorLanding() {
  useLandingSEO();
  return (
    <main
      data-testid="pa-landing-page"
      className="bg-white text-[#0A0A0A] antialiased overflow-x-hidden"
    >
      <L2Navbar />
      <L2Hero />
      <BloccoSostegno />
      <BloccoOrientamento />
      <BloccoGuida />
      <ParallaxConclusivo />
      <L2Adesione />
      <Footer />
    </main>
  );
}
